import { ActionIcon, Button, Group, NumberInput, Select, Table, Text, TextInput } from '@mantine/core'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import type { OrdenItemEntrada } from '@serkat/shared'
import { aSelect, useCatalogos } from '../../../api/catalogos'
import { moneda } from '../../../compartido/formato'

interface Props {
  items: OrdenItemEntrada[]
  monedaCodigo: 'PEN' | 'USD'
  igvPorcentaje: number
  /** Tasa de retención, o `null` si la orden no está sujeta. */
  retencionPorcentaje: number | null
  errores: Record<string, string | undefined>
  onCambiar: (items: OrdenItemEntrada[]) => void
}

/**
 * Ítems con precio. Los totales se muestran calculados en vivo para que el
 * usuario los vea al escribir, pero NO se envían: los recalcula la base al
 * guardar, y esa es la cifra que vale.
 */
export function TablaItemsOrden(
  { items, monedaCodigo, igvPorcentaje, retencionPorcentaje, errores, onCambiar }: Props,
) {
  const { data: catalogos } = useCatalogos()
  const unidades = aSelect(catalogos?.unidades)

  const subtotal = items.reduce((s, i) => s + (i.cantidad || 0) * (i.precioUnitario || 0), 0)
  const igv = Math.round(subtotal * igvPorcentaje * 100) / 100
  const total = subtotal + igv
  // La retención no se resta del total: el proveedor factura el total y ese es
  // el compromiso. Lo que cambia es cuánto se le transfiere.
  const retencion =
    retencionPorcentaje == null ? 0 : Math.round(total * retencionPorcentaje * 100) / 100
  const neto = total - retencion

  const renumerar = (l: OrdenItemEntrada[]) => l.map((it, i) => ({ ...it, itemNumero: i + 1 }))

  function agregar() {
    onCambiar(
      renumerar([
        ...items,
        {
          itemNumero: items.length + 1, descripcion: '',
          unidadId: catalogos?.unidades[0]?.id ?? 0,
          cantidad: 1, precioUnitario: 0, insumoId: null,
        },
      ]),
    )
  }

  function editar(i: number, campo: keyof OrdenItemEntrada, valor: unknown) {
    onCambiar(items.map((it, k) => (k === i ? { ...it, [campo]: valor } : it)))
  }

  return (
    <div>
      <Group justify="space-between" mb="xs">
        <Text fw={550} size="sm">Ítems de la orden</Text>
        <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={agregar}>
          Agregar ítem
        </Button>
      </Group>

      {errores['items'] && <Text c="red" size="xs" mb="xs">{errores['items']}</Text>}

      <Table.ScrollContainer minWidth={720}>
        <Table withTableBorder verticalSpacing={4}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>#</Table.Th>
              <Table.Th>Descripción</Table.Th>
              <Table.Th w={120}>Unidad</Table.Th>
              <Table.Th w={100}>Cantidad</Table.Th>
              <Table.Th w={130}>P. unitario</Table.Th>
              <Table.Th w={120} ta="right">Parcial</Table.Th>
              <Table.Th w={40} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed" size="sm" ta="center" py="sm">Sin ítems. Agregue al menos uno.</Text>
                </Table.Td>
              </Table.Tr>
            )}
            {items.map((it, i) => (
              <Table.Tr key={i}>
                <Table.Td ta="center" c="dimmed">{it.itemNumero}</Table.Td>
                <Table.Td>
                  <TextInput size="xs" value={it.descripcion}
                    onChange={(e) => editar(i, 'descripcion', e.currentTarget.value)}
                    error={Boolean(errores[`items.${i}.descripcion`])} />
                </Table.Td>
                <Table.Td>
                  <Select size="xs" data={unidades} searchable
                    value={it.unidadId ? String(it.unidadId) : null}
                    onChange={(v) => editar(i, 'unidadId', Number(v))}
                    error={Boolean(errores[`items.${i}.unidadId`])} />
                </Table.Td>
                <Table.Td>
                  <NumberInput size="xs" min={0.0001} decimalScale={4} hideControls
                    value={it.cantidad}
                    onChange={(v) => editar(i, 'cantidad', Number(v) || 0)}
                    error={Boolean(errores[`items.${i}.cantidad`])} />
                </Table.Td>
                <Table.Td>
                  <NumberInput size="xs" min={0} decimalScale={4} hideControls thousandSeparator
                    value={it.precioUnitario}
                    onChange={(v) => editar(i, 'precioUnitario', Number(v) || 0)}
                    error={Boolean(errores[`items.${i}.precioUnitario`])} />
                </Table.Td>
                <Table.Td ta="right" ff="monospace" fz="xs">
                  {moneda((it.cantidad || 0) * (it.precioUnitario || 0), monedaCodigo)}
                </Table.Td>
                <Table.Td>
                  <ActionIcon variant="subtle" color="red"
                    onClick={() => onCambiar(renumerar(items.filter((_, k) => k !== i)))}>
                    <IconTrash size={15} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Td colSpan={5} ta="right" fz="sm">Subtotal</Table.Td>
              <Table.Td ta="right" ff="monospace" fz="sm">{moneda(subtotal, monedaCodigo)}</Table.Td>
              <Table.Td />
            </Table.Tr>
            <Table.Tr>
              <Table.Td colSpan={5} ta="right" fz="sm" c="dimmed">
                IGV {(igvPorcentaje * 100).toFixed(0)}%
              </Table.Td>
              <Table.Td ta="right" ff="monospace" fz="sm" c="dimmed">{moneda(igv, monedaCodigo)}</Table.Td>
              <Table.Td />
            </Table.Tr>
            <Table.Tr>
              <Table.Td colSpan={5} ta="right" fw={650}>Total</Table.Td>
              <Table.Td ta="right" ff="monospace" fw={650}>{moneda(total, monedaCodigo)}</Table.Td>
              <Table.Td />
            </Table.Tr>
            {retencionPorcentaje != null && (
              <>
                <Table.Tr>
                  <Table.Td colSpan={5} ta="right" fz="sm" c="dimmed">
                    Retención {(retencionPorcentaje * 100).toFixed(0)}%
                  </Table.Td>
                  <Table.Td ta="right" ff="monospace" fz="sm" c="dimmed">
                    −{moneda(retencion, monedaCodigo)}
                  </Table.Td>
                  <Table.Td />
                </Table.Tr>
                <Table.Tr>
                  <Table.Td colSpan={5} ta="right" fw={650}>Neto a pagar</Table.Td>
                  <Table.Td ta="right" ff="monospace" fw={650}>
                    {moneda(neto, monedaCodigo)}
                  </Table.Td>
                  <Table.Td />
                </Table.Tr>
              </>
            )}
          </Table.Tfoot>
        </Table>
      </Table.ScrollContainer>

      <Text size="xs" c="dimmed" mt={6}>
        Los totales definitivos los calcula el sistema al guardar.
      </Text>
    </div>
  )
}
